/**
 * AI models — a Configuration tab, admins only.
 *
 * The model catalogue, following GftUs.SolutionsLab.FordArena's settings:
 * models on Azure AI Foundry and the Anthropic API, with their provider
 * settings and prices. The house list layout inside Configuration's card:
 * toolbar (search, provider and status filters, Add model), a plain-header
 * table and the pagination; the list is small, so it all runs in the browser.
 * Edit and Test are icon buttons on each row, with instant tooltips. No delete: disabling is the
 * reversible equivalent, and the usage ledger keeps referring to the key.
 * Which model each process uses is set on its feature's tab (ProcessAssignments).
 */

import { useMemo, useState } from 'react';
import { Bot, Plus, Search } from 'lucide-react';

import { useHostings, useModels } from '@/api/queries';
import type { AiModel, Hosting } from '@/api/types';
import { MultiSelect } from '@/ui/MultiSelect';
import { Pagination } from '@/ui/Pagination';
import { SortHeader } from '@/ui/SortHeader';
import { LoadFailure, LoadingCard } from '@/ui/StateCard';
import { useSession } from '@/session/SessionContext';

import { ModelDialog } from './ModelDialog';
import { columnCount, STATUSES, statusOf, type RowAccess } from './modelList';
import { ModelRow } from './ModelRow';
import { useModelList, type ModelList } from './useModelList';

type Editing = AiModel | 'new' | null;

export function AiModelsTab() {
  const models = useModels();
  const hostings = useHostings();
  const { can } = useSession();
  const access: RowAccess = { manage: can('ai.models.manage'), test: can('ai.models.test') };
  const [editing, setEditing] = useState<Editing>(null);

  const rows = useMemo(() => models.data ?? [], [models.data]);
  const hostingLabel = useMemo(
    () => Object.fromEntries((hostings.data ?? []).map((h) => [h.id, h.label])) as Record<string, string>,
    [hostings.data],
  );
  const list = useModelList(rows, hostingLabel);

  if (models.isPending || hostings.isPending) return <LoadingCard label="Loading AI models" />;
  const failure = models.error ?? hostings.error;
  if (failure || !hostings.data) {
    return (
      <LoadFailure
        error={failure ?? new Error('No providers.')}
        onRetry={() => {
          void models.refetch();
          void hostings.refetch();
        }}
      />
    );
  }

  return (
    <AiModelsView
      rows={rows}
      hostings={hostings.data}
      hostingLabel={hostingLabel}
      access={access}
      list={list}
      editing={editing}
      setEditing={setEditing}
    />
  );
}

function AiModelsView({
  rows,
  hostings,
  hostingLabel,
  access,
  list,
  editing,
  setEditing,
}: {
  rows: AiModel[];
  hostings: Hosting[];
  hostingLabel: Record<string, string>;
  access: RowAccess;
  list: ModelList;
  editing: Editing;
  setEditing: (editing: Editing) => void;
}) {
  return (
    <>
      <ModelToolbar rows={rows} hostings={hostings} list={list} canAdd={access.manage} onAdd={() => setEditing('new')} />
      <ModelTable rows={rows} hostingLabel={hostingLabel} access={access} list={list} onEdit={setEditing} />
      {list.visible.length > 0 && (
        <Pagination
          page={list.page}
          pageSize={list.pageSize}
          total={list.visible.length}
          noun="models"
          onPage={list.setPage}
          onPageSize={list.setPageSize}
        />
      )}

      {editing && (
        <ModelDialog hostings={hostings} model={editing === 'new' ? undefined : editing} onClose={() => setEditing(null)} />
      )}
    </>
  );
}

function ModelToolbar({
  rows,
  hostings,
  list,
  canAdd,
  onAdd,
}: {
  rows: AiModel[];
  hostings: Hosting[];
  list: ModelList;
  canAdd: boolean;
  onAdd: () => void;
}) {
  const providerCount = (id: string) => rows.filter((m) => m.hosting === id).length;
  const statusCount = (s: string) => rows.filter((m) => statusOf(m) === s).length;
  return (
    <div className="toolbar filter-panel">
      <div className="search-field">
        <Search aria-hidden="true" />
        <input
          type="search"
          value={list.search}
          placeholder="Search by name or model id"
          aria-label="Search by name or model id"
          onChange={(e) => list.setSearch(e.target.value)}
        />
      </div>
      <MultiSelect
        label="Filter by provider"
        allLabel="All providers"
        noun="providers"
        options={hostings.map((h) => ({ value: h.id, label: h.label, meta: providerCount(h.id) }))}
        value={list.providers}
        onChange={list.setProviders}
      />
      <MultiSelect
        label="Filter by status"
        allLabel="All statuses"
        noun="statuses"
        options={STATUSES.map((s) => ({ ...s, meta: statusCount(s.value) }))}
        value={list.statuses}
        onChange={list.setStatuses}
      />
      {canAdd && (
        <button type="button" className="btn btn-primary" onClick={onAdd}>
          <Plus aria-hidden="true" /> Add model
        </button>
      )}
    </div>
  );
}

function ModelTable({
  rows,
  hostingLabel,
  access,
  list,
  onEdit,
}: {
  rows: AiModel[];
  hostingLabel: Record<string, string>;
  access: RowAccess;
  list: ModelList;
  onEdit: (model: AiModel) => void;
}) {
  const columns = columnCount(access);
  const { key: sort, order } = list.sort;
  const onSort = list.onSort;
  return (
    <div className="table-scroll">
      <table className="data zebra compact">
        <thead>
          <tr>
            <SortHeader label="Display name" sortKey="display_name" sort={sort} order={order} onSort={onSort} />
            <SortHeader label="Provider" sortKey="provider" sort={sort} order={order} onSort={onSort} />
            <SortHeader label="Model" sortKey="model_id" sort={sort} order={order} onSort={onSort} />
            <SortHeader label="Input / 1M" sortKey="price_input" sort={sort} order={order} onSort={onSort} className="num" />
            <SortHeader label="Output / 1M" sortKey="price_output" sort={sort} order={order} onSort={onSort} className="num" />
            <SortHeader label="Enabled" sortKey="enabled" sort={sort} order={order} onSort={onSort} />
            {columns === 7 && <th scope="col"><span className="sr-only">Actions</span></th>}
          </tr>
        </thead>
        <tbody>
          {list.pageRows.length === 0 ? (
            <EmptyRow columns={columns} hasModels={rows.length > 0} />
          ) : (
            list.pageRows.map((model) => (
              <ModelRow
                key={model.key}
                model={model}
                hostingLabel={hostingLabel[model.hosting] ?? model.hosting}
                access={access}
                columns={columns}
                onEdit={() => onEdit(model)}
              />
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function EmptyRow({ columns, hasModels }: { columns: number; hasModels: boolean }) {
  return (
    <tr>
      <td colSpan={columns}>
        <div className="empty">
          <div className="e-icon"><Bot aria-hidden="true" /></div>
          {hasModels ? (
            <>
              <h3>No models match</h3>
              <p>Clear the search, the provider or the status filter to see the rest.</p>
            </>
          ) : (
            <>
              <h3>No models yet</h3>
              <p>Add a model from Azure AI Foundry or the Anthropic API. Then assign it to each AI process under Process assignments.</p>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}
